import { useMemo, useCallback } from 'react';
import { CellClassParams, DisplayedColumnsChangedEvent, BodyScrollEvent } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { BASE_GRID_OPTIONS } from '@/components/AgGrid/helpers/baseGridOptions';

const useAgGridProps = (): Partial<AgGridReactProps> => {
  const getCellClass = useCallback((params: CellClassParams) => {
    const classes: string[] = [];

    if (params.node.level === 1) {
      const displayedColumns = params.api.getAllDisplayedColumns();
      const nonPinnedColumns = displayedColumns.filter((col) => !col.getPinned());

      if (nonPinnedColumns.length > 0) {
        if (nonPinnedColumns[0].getColId() === params.column.getColId()) {
          classes.push('custom-first-visible');
        }
        if (nonPinnedColumns[nonPinnedColumns.length - 1].getColId() === params.column.getColId()) {
          classes.push('custom-last-visible');
        }
      }
    }

    return classes;
  }, []);

  const onDisplayedColumnsChanged = useCallback((params: DisplayedColumnsChangedEvent) => {
    params.api.refreshCells({ force: true });
  }, []);

  const onBodyScroll = useCallback((params: BodyScrollEvent) => {
    // Only refresh when horizontal scroll occurs (column virtualization)
    if (params.direction === 'horizontal') {
      params.api.refreshCells({ force: true });
    }
  }, []);

  return useMemo<Partial<AgGridReactProps>>(
    () => ({
      ...BASE_GRID_OPTIONS,
      rowModelType: 'serverSide',
      treeData: true,
      rowHeight: 72,
      suppressServerSideFullWidthLoadingRow: true,
      suppressContextMenu: true,
      suppressMaxRenderedRowRestriction: true,
      alwaysShowVerticalScroll: true,
      pagination: false,
      onDisplayedColumnsChanged,
      onBodyScroll,
      defaultColDef: {
        filter: false,
        resizable: true,
        minWidth: 120,
        flex: 1,
        suppressMovable: true,
        suppressNavigable: true,
        headerComponentParams: {
          enablePinning: false,
        },
        cellClass: getCellClass,
      },
    }),
    [getCellClass, onDisplayedColumnsChanged, onBodyScroll],
  );
};
export default useAgGridProps;
